/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PersonalityTab.tsx
 🗓️ Created: 2026-01-21
 🧩 Description:
    AI Agent Personality & Tone configuration tab.
    Follows OpenAI Realtime Prompting Guide recommendations.
────────────────────────────────────────────────────────────────*/

import { useI18n } from '../../../hooks/useI18n';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';

const VOICES = ['alloy', 'ash', 'ballad', 'cedar', 'coral', 'echo', 'marin', 'sage', 'shimmer', 'verse'];

interface PersonalityTabProps {
    formData: {
        voice?: string;
        personality?: string;
        tone_of_voice?: string;
        language_level?: string;
        speaking_style?: string;
        response_length?: string;
        filler_words?: boolean;
        reasoning_effort?: string;
        reduce_repetition?: boolean;
    };
    onChange: (field: string, value: unknown) => void;
    agentType?: 'voice' | 'chat';
}

export default function PersonalityTab({ formData, onChange, agentType = 'voice' }: PersonalityTabProps) {
    const { t } = useI18n();

    const personalityOptions = [
        { value: 'friendly', label: t('aiAgents.personalityTab.options.friendly') || 'Friendly & Approachable' },
        { value: 'professional', label: t('aiAgents.personalityTab.options.professional') || 'Professional & Polished' },
        { value: 'calm', label: t('aiAgents.personalityTab.options.calm') || 'Calm & Patient' },
        { value: 'enthusiastic', label: t('aiAgents.personalityTab.options.enthusiastic') || 'Enthusiastic & Energetic' },
        { value: 'empathetic', label: t('aiAgents.personalityTab.options.empathetic') || 'Empathetic & Supportive' }
    ];

    const toneOptions = [
        { value: 'warm', label: t('aiAgents.personalityTab.options.warm') || 'Warm' },
        { value: 'confident', label: t('aiAgents.personalityTab.options.confident') || 'Confident' },
        { value: 'neutral', label: t('aiAgents.personalityTab.options.neutral') || 'Neutral' },
        { value: 'encouraging', label: t('aiAgents.personalityTab.options.encouraging') || 'Encouraging' },
        { value: 'direct', label: t('aiAgents.personalityTab.options.direct') || 'Direct' }
    ];

    const languageLevelOptions = [
        { value: 'casual', label: t('aiAgents.personalityTab.options.casual') || 'Casual (informal, relaxed)' },
        { value: 'conversational', label: t('aiAgents.personalityTab.options.conversational') || 'Conversational (friendly & clear)' },
        { value: 'formal', label: t('aiAgents.personalityTab.options.formal') || 'Formal (professional, polished)' },
        { value: 'technical', label: t('aiAgents.personalityTab.options.technical') || 'Technical (domain-specific)' }
    ];

    const speakingStyleOptions = [
        { value: 'natural', label: t('aiAgents.personalityTab.options.natural') || 'Natural (balanced pace)' },
        { value: 'fast_paced', label: t('aiAgents.personalityTab.options.fast_paced') || 'Fast-paced (quick & clear)' },
        { value: 'slow_clear', label: t('aiAgents.personalityTab.options.slow_clear') || 'Slow & Clear (emphasis on clarity)' },
        { value: 'expressive', label: t('aiAgents.personalityTab.options.expressive') || 'Expressive (dynamic emotions)' }
    ];

    const responseLengthOptions = [
        { value: 'brief', label: t('aiAgents.personalityTab.options.brief') || 'Brief (1-2 sentences)' },
        { value: 'standard', label: t('aiAgents.personalityTab.options.standard') || 'Standard (2-3 sentences)' },
        { value: 'detailed', label: t('aiAgents.personalityTab.options.detailed') || 'Detailed (3-4 sentences)' }
    ];

    const reasoningEffortOptions = [
        { value: 'low', label: t('aiAgents.personalityTab.options.reasoningLow') || 'Low (Fastest)' },
        { value: 'medium', label: t('aiAgents.personalityTab.options.reasoningMedium') || 'Medium (Balanced)' },
        { value: 'high', label: t('aiAgents.personalityTab.options.reasoningHigh') || 'High (Most Accurate)' }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
            {/* Row 1: Voice + Personality */}
            {agentType !== 'chat' && (
                <>
                    <SimpleTooltip content={t('aiAgents.personalityTab.voiceTooltip') || 'The AI voice persona used for speech synthesis. Each voice has unique characteristics - try different voices to find the best fit for your agent\'s personality.'} position="top">
                        <Label className="text-left">
                            {t('aiAgents.form.voice') || 'Voice'}
                        </Label>
                    </SimpleTooltip>
                    <select
                        value={formData.voice || 'alloy'}
                        onChange={(e) => onChange('voice', e.target.value)}
                        className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                        {VOICES.map(v => (
                            <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                        ))}
                    </select>
                </>
            )}

            <SimpleTooltip content={t('aiAgents.personalityTab.personalityTooltip') || `The core personality trait that defines how the AI agent interacts with ${agentType === 'chat' ? 'users' : 'callers'}. This affects the emotional tone, approach, and overall demeanor during conversations.`} position="top">
                <Label htmlFor="personality" className="text-left">
                    {t('aiAgents.personalityTab.personality') || 'Agent Personality'}
                </Label>
            </SimpleTooltip>
            <select
                id="personality"
                value={formData.personality || 'friendly'}
                onChange={(e) => onChange('personality', e.target.value)}
                className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
                {personalityOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>

            {/* Row 2: Tone of Voice + Language Level */}
            <SimpleTooltip content={t('aiAgents.personalityTab.toneTooltip') || 'The emotional delivery style of responses. Warm tones are friendly and reassuring; Confident tones are assertive and decisive; Direct tones are straightforward and efficient.'} position="top">
                <Label htmlFor="tone_of_voice" className="text-left">
                    {t('aiAgents.personalityTab.toneOfVoice') || 'Tone of Voice'}
                </Label>
            </SimpleTooltip>
            <select
                id="tone_of_voice"
                value={formData.tone_of_voice || 'warm'}
                onChange={(e) => onChange('tone_of_voice', e.target.value)}
                className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
                {toneOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>

            <SimpleTooltip content={t('aiAgents.personalityTab.languageLevelTooltip') || 'The formality level of language used. Casual uses everyday speech; Conversational is friendly but clear; Formal is professional and polished; Technical uses domain-specific terminology.'} position="top">
                <Label htmlFor="language_level" className="text-left">
                    {t('aiAgents.personalityTab.languageLevel') || 'Language Level'}
                </Label>
            </SimpleTooltip>
            <select
                id="language_level"
                value={formData.language_level || 'conversational'}
                onChange={(e) => onChange('language_level', e.target.value)}
                className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
                {languageLevelOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>

            {agentType !== 'chat' && (
                <>
                    <SimpleTooltip content={t('aiAgents.personalityTab.speakingStyleTooltip') || 'The pacing and delivery of audio responses. Natural provides balanced pacing; Fast-paced is quick and efficient; Slow & Clear emphasizes understanding; Expressive adds dynamic emotional variation.'} position="top">
                        <Label htmlFor="speaking_style" className="text-left">
                            {t('aiAgents.personalityTab.speakingStyle') || 'Speaking Style'}
                        </Label>
                    </SimpleTooltip>
                    <select
                        id="speaking_style"
                        value={formData.speaking_style || 'natural'}
                        onChange={(e) => onChange('speaking_style', e.target.value)}
                        className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                        {speakingStyleOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </>
            )}

            <SimpleTooltip content={t('aiAgents.personalityTab.responseLengthTooltip') || 'Controls how many sentences the AI uses per response turn. Brief (1-2 sentences) for quick exchanges; Standard (2-3 sentences) for balanced conversations; Detailed (3-4 sentences) for comprehensive explanations.'} position="top">
                <Label htmlFor="response_length" className="text-left">
                    {t('aiAgents.personalityTab.responseLength') || 'Response Length'}
                </Label>
            </SimpleTooltip>
            <select
                id="response_length"
                value={formData.response_length || 'standard'}
                onChange={(e) => onChange('response_length', e.target.value)}
                className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
                {responseLengthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>

            <SimpleTooltip content={t('aiAgents.personalityTab.reasoningEffortTooltip') || 'Level of reasoning for OpenAI Realtime 2.0. High reasoning effort is slower but more accurate. Low is faster.'} position="top">
                <Label htmlFor="reasoning_effort" className="text-left">
                    {t('aiAgents.personalityTab.reasoningEffort') || 'Reasoning Intensity'}
                </Label>
            </SimpleTooltip>
            <select
                id="reasoning_effort"
                value={formData.reasoning_effort || 'medium'}
                onChange={(e) => onChange('reasoning_effort', e.target.value)}
                className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
                {reasoningEffortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>

            {/* Row 4: Filler Words + Reduce Repetition */}
            {agentType !== 'chat' && (
                <>
                    <SimpleTooltip content={t('aiAgents.personalityTab.fillerWordsTooltip') || 'Allow OpenAI Realtime native preambles (filler words) like "um", "uh" before responses to reduce latency perception and make the agent sound more human.'} position="top">
                        <Label className="text-left">
                            {t('aiAgents.personalityTab.fillerWords') || 'Preambles (Nativo)'}
                        </Label>
                    </SimpleTooltip>
                    <Toggle
                        value={formData.filler_words ?? false}
                        onChange={(val) => onChange('filler_words', val)}
                    />
                </>
            )}

            <SimpleTooltip content={t('aiAgents.personalityTab.reduceRepetitionTooltip') || 'Enable variety constraints to prevent the AI from using the same phrases repeatedly. When enabled, the AI will vary its wording to sound more natural and less mechanical.'} position="top">
                <Label className="text-left">
                    {t('aiAgents.personalityTab.reduceRepetition') || 'Reduce Repetition'}
                </Label>
            </SimpleTooltip>
            <Toggle
                value={formData.reduce_repetition ?? true}
                onChange={(val) => onChange('reduce_repetition', val)}
            />
        </div>
    );
}
